import { useCallback } from 'react'
import { useSegwayRouter } from '@/features/shared/hooks/useSegwayRouter'
import NProgress from 'nprogress'
import { useIsMutating, useQueryClient } from '@tanstack/react-query'
import {
  SelectStoreMutationKey,
  useMutationSelectStore,
} from '@/features/shop/services/ShoppingStore/mutations'
import { useValidNextParam } from '@/features/shared/hooks/useValidNextParam'
import { useSetGuestStoreCookie } from '@/features/account/services/GuestUser/hooks'
import { type Promotion } from '@/features/shop/services/Campaigns/types'
import { type GuestStore } from '@/features/shop/services/ShoppingStore/types'
import { getIsZipOnlySignedInUser, getUser } from '@/features/account/services/User/utils'
import { getStoreParamsClient } from '@/features/shared/utils/dataFetching/storeParamsUtils'
import { getSearchRouteWithPromotion } from '@/features/cms/components/CMS/utils/promotion'
import { getRetailerUrl } from '@shared/constants/routes'
import { datadogRum } from '@datadog/browser-rum'
import { GHP_TO_RHP_DURATION } from '@/lib/datadog/vitals'
import { setCookie } from '@/features/shared/utils/cookies'
import { environment } from '@/features/shared/utils/environment'
import { USER_STORE_COOKIE } from '@/features/shop/services/ShoppingStore/cookies'
import { slimGuestStore } from '@/features/account/services/GuestUser/utils'
import { zipUserStoreQueryOptions } from '@/features/shop/services/ShoppingStore/queryOptions'

export const useGlobalHomeSelectStore = (promotion?: Promotion) => {
  const router = useSegwayRouter()
  const queryClient = useQueryClient()
  const { mutateAsync, isPending: isLoading, error } = useMutationSelectStore()
  const isSelectingStore = Boolean(useIsMutating({ mutationKey: [SelectStoreMutationKey] }))
  const nextRoute = useValidNextParam()
  const setGuestStoreCookie = useSetGuestStoreCookie()

  const selectStore = useCallback(
    async (store: GuestStore, destinationUrl?: string) => {
      const nonRHPRoute =
        destinationUrl ||
        (promotion && getSearchRouteWithPromotion(promotion, store.name)) ||
        nextRoute
      const retailerUrl = getRetailerUrl(store.name, { skipSsr: true })
      // retailer home page is the default route if none of the above routes are valid
      const route = nonRHPRoute || retailerUrl

      const user = getUser(queryClient)
      const isSameStore = getStoreParamsClient().store_id === store.id
      if (!nonRHPRoute) {
        datadogRum.startDurationVital(GHP_TO_RHP_DURATION, {
          description: 'How much time does the RHP take to load after user navigates from GHP?',
          context: {
            storeChanged: !isSameStore,
            storeId: store.id,
            guestTransition: !user,
          },
        })
      }
      if (isSameStore) {
        return router.push(route)
      }
      // prefetch the retailer URL via router if we are routing to the retailer home page
      if (!nonRHPRoute) router.prefetch(retailerUrl)

      // For zip-only user, we want to set the user_store cookie and redirect them to the next route
      if (getIsZipOnlySignedInUser(user)) {
        const storeData = slimGuestStore(store)
        setCookie(USER_STORE_COOKIE, storeData, {
          secure: environment() !== 'local',
          expires: 7,
        })
        queryClient.setQueryData(zipUserStoreQueryOptions.queryKey, storeData)
        return router.push(route)
      }

      if (user) {
        const addressId = user.default_shopping_address_id
        try {
          NProgress.start()
          await mutateAsync({ addressId, store, route })

          return
        } catch {
          NProgress.done()
        }
      }

      setGuestStoreCookie(store)
      return router.push(route)
    },
    [promotion, nextRoute, queryClient, setGuestStoreCookie, router, mutateAsync],
  )

  return { isLoading, selectStore, isSelectingStore, error }
}
